import { getConfig } from '../lib/runtime-config';
import { authStore } from './auth.service';

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip the X-Api-Token header (used for login + reset-password endpoints). */
  anonymous?: boolean;
}

/**
 * Builds an absolute URL by joining the runtime base uri with the given path.
 */
export function buildUrl(path: string): string {
  const cfg = getConfig();
  const base = cfg.apis.baseUri.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/**
 * Issues a JSON request to the tkw-api and returns the parsed response.
 * Throws ApiError on non-2xx responses (with status + body).
 */
export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const cfg = getConfig();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Api-Tenantid': String(cfg.tenantId),
    ...(options.headers || {})
  };

  if (options.body !== undefined && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (!options.anonymous) {
    const token = authStore.getToken();
    if (token) {
      headers['X-Api-Token'] = token;
    }
  }

  const response = await fetch(buildUrl(path), {
    method: options.method || 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    cache: 'no-store'
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }

  if (!response.ok) {
    const error: ApiError = Object.assign(new Error(`api error: ${response.status}`), {
      status: response.status,
      body: parsed
    });
    throw error;
  }

  return parsed as T;
}
